import FollowUpsDue from '@components/Common/FollowUpsDue';
import { Panel } from '@components/Common/Panel';
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import {
  hrManagedJobs,
  hrWorklistApplications,
  interviewsForEmployer,
  markInterviewOutcomeMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { jobStatusLabel, jobTypeLabel } from '@constants/jobEnums';
import { awaitingFeedback } from '@constants/applicationStatus';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import EmptyState from '@components/Common/EmptyState';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Box, Button, Chip, Typography } from '@mui/material';
import CountUp from '@components/Common/CountUp';
import type { RootState } from '@store/rootReducer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import { formatDateTime as formatDateTimeShared } from '@helpers/date';


interface JobCard {
  jobId: string;
  title: string;
  companyName: string;
  employerId: string;
  jobType: string | null;
  jobStatus: string | null;
  candidates: number;
  awaitingFeedback: number;
}

interface UpcomingInterview {
  _id: string;
  applicationId: string;
  clientName: string;
  round: number | null;
  scheduledAt: string;
  meetingLink: string | null;
  meetingProvider: string | null;
  outcome: string | null;
}

// A live/active role is jobStatus OPEN (enum is OPEN | CLOSED | EXPIRED).
const isActiveJob = (status?: string | null) => (status ?? '').toLowerCase() === 'open';

const formatDateTime = (iso: string): string => formatDateTimeShared(iso, iso);

const MyAccounts: React.FC = () => {
  const navigate = useNavigate();
  const userId = useSelector(
    (state: RootState) => state.loginUserProfile.profile?._id as string | undefined,
  );

  const [jobs, setJobs] = useState<JobCard[]>([]);
  const [interviews, setInterviews] = useState<UpcomingInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Without an id there is no worklist to ask for. Sending an empty one is worse than
    // sending nothing: the backend drops an empty filter value instead of matching nothing,
    // so the query would come back with every MANAGED job on the platform.
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      // The HR's assigned MANAGED jobs and their scoped applications, in parallel.
      const [jobsRes, appsRes] = await Promise.all([
        fetchGraphQLApiData(hrManagedJobs(0, 200, userId)).catch(() => null),
        fetchGraphQLApiData(hrWorklistApplications(0, 1000)).catch(() => null),
      ]);

      const jobRows = (jobsRes?.data?.getAllJobs?.content ?? []).filter(Boolean) as any[];
      const appRows = (appsRes?.data?.getAllJobApplications?.content ?? []).filter(
        Boolean,
      ) as any[];

      // Tally candidates + awaiting-feedback per job; keep the HR's own application ids
      // so cross-employer interviews can be scoped back to this HR's jobs.
      const byJob = new Map<string, { total: number; awaiting: number }>();
      const appIds = new Set<string>();
      appRows.forEach((a) => {
        if (a._id) appIds.add(a._id);
        const jid = a.jobId;
        if (!jid) return;
        const c = byJob.get(jid) ?? { total: 0, awaiting: 0 };
        c.total += 1;
        if (awaitingFeedback(a.status, a.subStatus)) c.awaiting += 1;
        byJob.set(jid, c);
      });

      const cards: JobCard[] = jobRows.map((j) => ({
        jobId: j._id,
        title: j.title || 'Untitled role',
        companyName: j.company?.companyName || j.companyName || 'Unnamed company',
        employerId: j.employerId || '',
        jobType: j.jobType ?? null,
        jobStatus: j.jobStatus ?? null,
        candidates: byJob.get(j._id)?.total ?? 0,
        awaitingFeedback: byJob.get(j._id)?.awaiting ?? 0,
      }));
      setJobs(cards);

      // Interviews are keyed by employer; fetch once per distinct employer, then keep only
      // those belonging to this HR's applications (an employer's jobs may split across HRs).
      const employerNames = new Map<string, string>();
      cards.forEach((c) => {
        if (c.employerId) employerNames.set(c.employerId, c.companyName);
      });
      const ivResults = await Promise.all(
        Array.from(employerNames.keys()).map((empId) =>
          fetchGraphQLApiData(interviewsForEmployer(empId))
            .then((r) => ({ empId, ivs: (r?.data?.getInterviewsForEmployer ?? []) as any[] }))
            .catch(() => ({ empId, ivs: [] as any[] })),
        ),
      );

      const cutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
      const upcoming: UpcomingInterview[] = ivResults
        .flatMap(({ empId, ivs }) =>
          ivs
            .filter((iv) => appIds.has(iv.applicationId))
            .map((iv) => ({
              _id: iv._id,
              applicationId: iv.applicationId,
              clientName: employerNames.get(empId) || 'Client',
              round: iv.round ?? null,
              scheduledAt: iv.scheduledAt ?? '',
              meetingLink: iv.meetingLink ?? null,
              meetingProvider: iv.meetingProvider ?? null,
              outcome: iv.outcome ?? null,
            })),
        )
        .filter((iv) => {
          const t = Date.parse(iv.scheduledAt);
          return Number.isFinite(t) ? t >= cutoff : true;
        })
        .sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt));
      setInterviews(upcoming);
    } catch {
      setError('Failed to load your worklist. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
    else setLoading(false);
  }, [userId, load]);

  const summary = useMemo(
    () => ({
      jobs: jobs.length,
      liveRoles: jobs.filter((j) => isActiveJob(j.jobStatus)).length,
      awaitingFeedback: jobs.reduce((s, j) => s + j.awaitingFeedback, 0),
      interviews: interviews.length,
    }),
    [jobs, interviews],
  );

  const markOutcome = async (interviewId: string, outcome: string) => {
    try {
      await fetchGraphQLApiData(markInterviewOutcomeMutation(interviewId, outcome), {
        showStatus: true,
        message: 'Interview outcome recorded',
      });
      setInterviews((prev) =>
        prev.map((iv) => (iv._id === interviewId ? { ...iv, outcome } : iv)),
      );
    } catch {
      /* toast surfaced by the helper */
    }
  };

  // Deep-link straight to the merged applications list (not /job-applications, whose redirect
  // drops the query) pre-filtered to this exact job. jobLabel gives the filter chip its text.
  const viewCandidates = (job: JobCard) =>
    navigate({
      pathname: '/applications/list',
      search: `?jobId=${encodeURIComponent(job.jobId)}&jobLabel=${encodeURIComponent(
        [job.title, job.companyName].filter(Boolean).join(' · '),
      )}`,
    });

  const renderBody = () => {
    if (error) return <ErrorMessage page message={error} />;
    if (loading) return <SkeletonLoader />;
    if (jobs.length === 0) {
      return (
        <EmptyState
          icon={<WorkOutlineOutlinedIcon />}
          title="No jobs assigned to you yet"
          body="A Super Admin assigns premium jobs to you from the employer's Job assignments panel."
        />
      );
    }

    return (
      <Box className="ma-content">
        <Box className="ma-stats cc-motion-stagger">
          <Panel variant="stat" className="ma-stat">
            <WorkOutlineOutlinedIcon className="ma-stat-icon" />
            <Box>
              <Typography className="ma-stat-value"><CountUp value={summary.jobs} /></Typography>
              <Typography className="ma-stat-label">Jobs assigned</Typography>
            </Box>
          </Panel>
          <Panel variant="stat" className="ma-stat">
            <CheckCircleOutlineOutlinedIcon className="ma-stat-icon" />
            <Box>
              <Typography className="ma-stat-value"><CountUp value={summary.liveRoles} /></Typography>
              <Typography className="ma-stat-label">Live roles</Typography>
            </Box>
          </Panel>
          <Panel variant="stat" className="ma-stat ma-stat-accent">
            <PeopleAltOutlinedIcon className="ma-stat-icon" />
            <Box>
              <Typography className="ma-stat-value"><CountUp value={summary.awaitingFeedback} /></Typography>
              <Typography className="ma-stat-label">Awaiting feedback</Typography>
            </Box>
          </Panel>
          <Panel variant="stat" className="ma-stat">
            <VideocamOutlinedIcon className="ma-stat-icon" />
            <Box>
              <Typography className="ma-stat-value"><CountUp value={summary.interviews} /></Typography>
              <Typography className="ma-stat-label">Upcoming interviews</Typography>
            </Box>
          </Panel>
        </Box>

        <FollowUpsDue />

        <Typography className="ma-section-title">Assigned jobs</Typography>
        <Box className="ma-cards">
          {jobs.map((j) => (
            <Box key={j.jobId} className="ma-card">
              <Box className="ma-card-head">
                <Typography className="ma-card-title">{j.title}</Typography>
                {j.awaitingFeedback > 0 && (
                  <Chip
                    className="ma-card-badge"
                    size="small"
                    label={`${j.awaitingFeedback} awaiting feedback`}
                  />
                )}
              </Box>
              <Typography className="ma-card-contact" variant="body2">
                {j.companyName}
                {j.jobType ? ` · ${jobTypeLabel(j.jobType)}` : ''}
                {!isActiveJob(j.jobStatus) && j.jobStatus ? ` · ${jobStatusLabel(j.jobStatus)}` : ''}
              </Typography>
              <Box className="ma-card-metrics">
                <span>
                  <strong>{j.candidates}</strong> candidates
                </span>
                <span>
                  <strong>{j.awaitingFeedback}</strong> awaiting
                </span>
              </Box>
              <Button
                className="ma-card-action"
                size="small"
                variant="outlined"
                onClick={() => viewCandidates(j)}
              >
                View candidates
              </Button>
            </Box>
          ))}
        </Box>

        <Typography className="ma-section-title">Upcoming interviews</Typography>
        {interviews.length === 0 ? (
          <Typography className="ma-interviews-empty" variant="body2">
            No upcoming interviews.
          </Typography>
        ) : (
          <Box className="ma-interviews">
            {interviews.map((iv) => (
              <Box key={iv._id} className="ma-interview">
                <Box className="ma-interview-main">
                  <Typography className="ma-interview-client">{iv.clientName}</Typography>
                  <Typography className="ma-interview-meta" variant="body2">
                    {iv.round ? `Round ${iv.round} · ` : ''}
                    {formatDateTime(iv.scheduledAt)}
                    {iv.meetingProvider ? ` · ${iv.meetingProvider}` : ''}
                  </Typography>
                </Box>
                <Box className="ma-interview-actions">
                  {iv.outcome ? (
                    <Chip
                      size="small"
                      color={iv.outcome === 'NO_SHOW' ? 'error' : iv.outcome === 'ATTENDED' ? 'success' : 'default'}
                      label={iv.outcome === 'NO_SHOW' ? 'No-show' : iv.outcome.charAt(0) + iv.outcome.slice(1).toLowerCase()}
                    />
                  ) : Date.parse(iv.scheduledAt) <= Date.now() ? (
                    <>
                      <Button size="small" variant="outlined" color="success" onClick={() => markOutcome(iv._id, 'ATTENDED')}>
                        Attended
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => markOutcome(iv._id, 'NO_SHOW')}>
                        No-show
                      </Button>
                    </>
                  ) : iv.meetingLink ? (
                    <Button
                      size="small"
                      variant="contained"
                      href={iv.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join
                    </Button>
                  ) : (
                    <Chip size="small" label="Link pending" />
                  )}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(`/job-applications/${iv.applicationId}`)}
                  >
                    View candidate
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className="my-accounts-container">
      <CRMPageTemplate
        title="Premium Hiring Clients"
        icon={<WorkOutlineOutlinedIcon />}
        totalCount={jobs.length}
      >
        {renderBody()}
      </CRMPageTemplate>
    </Box>
  );
};

export default MyAccounts;
